import { useCallback, useEffect, useState } from "react";
import type { ClientReviewStatusV1 } from "@enrein/shared-contracts/approval-change";
import { ClientLinkIdSchema, RunIdSchema } from "@enrein/shared-contracts";
import type { StudioDepartureKitStatusV1, StudioProjectLifecycleSummaryV1 } from "@enrein/shared-contracts";
import type { StudioProjectSummaryV1 } from "@enrein/shared-contracts/studio-dashboard";
import type { StudioApiClient } from "../../../api.js";
import type { PollingState } from "../../../usePolling.js";
import { readProjectDisplayName } from "../../../project-display-name-store.js";

const LIFECYCLE_STUB_PREVIEW_VERSION_ID = "0".repeat(64);
const LIFECYCLE_STUB_CLIENT_LINK_ID = ClientLinkIdSchema.parse("lifecycle-summary");

export function reviewFromLifecycleSummary(
  summary: StudioProjectLifecycleSummaryV1,
): ClientReviewStatusV1 {
  return {
    schemaId: "studio.clientReviewStatus.v1",
    projectId: summary.projectId,
    approvalMatchesLatestPassingArtifact: true,
    clientLink: null,
    latestApproval: summary.flags.hasApproval
      ? {
          approvalId: "lifecycle-summary",
          clientLinkId: LIFECYCLE_STUB_CLIENT_LINK_ID,
          status: "APPROVED",
          approvedByName: "Client",
          approvedAt: summary.generatedAt,
        }
      : null,
    latestChangeRequest: null,
    latestPreview: summary.flags.hasPreview
      ? {
          previewVersionId: LIFECYCLE_STUB_PREVIEW_VERSION_ID,
          previewStatus: "READY",
          updatedAt: summary.generatedAt,
        }
      : null,
  };
}

export function departureKitFromLifecycleSummary(
  summary: StudioProjectLifecycleSummaryV1,
): StudioDepartureKitStatusV1 | null {
  if (!summary.flags.hasApproval && !summary.flags.packaging && !summary.flags.handoffReady) {
    return null;
  }
  const issuanceStatus = summary.flags.handoffReady
    ? "READY"
    : summary.flags.packaging
      ? "EXPORT_QUEUED"
      : "ELIGIBLE";
  return {
    schemaVersion: "studio-departure-kit-status.v1.0",
    agencyId: summary.agencyId,
    projectId: summary.projectId,
    runId: summary.exportRunId ? RunIdSchema.parse(summary.exportRunId) : undefined,
    issuanceStatus,
    passingVsApproved: null,
    downloadAvailable: summary.flags.handoffReady,
  };
}

export interface StudioProjectsEnrichment {
  readonly projectReviews: Readonly<Record<string, ClientReviewStatusV1 | null>>;
  readonly projectDepartureKits: Readonly<Record<string, StudioDepartureKitStatusV1 | null>>;
  readonly projectBusinessNames: Readonly<Record<string, string | null>>;
  readonly projectLifecycleSummaries: Readonly<Record<string, StudioProjectLifecycleSummaryV1 | null>>;
  readonly patchProjectEnrichment: (
    projectId: string,
    patch: {
      readonly review?: ClientReviewStatusV1 | null;
      readonly departureKit?: StudioDepartureKitStatusV1 | null;
      readonly businessName?: string | null;
      readonly lifecycleSummary?: StudioProjectLifecycleSummaryV1 | null;
    },
  ) => void;
}

export function useStudioProjectsEnrichment(
  api: StudioApiClient,
  projectsState: PollingState<StudioProjectSummaryV1[]>,
): StudioProjectsEnrichment {
  const [projectReviews, setProjectReviews] = useState<Record<string, ClientReviewStatusV1 | null>>({});
  const [projectDepartureKits, setProjectDepartureKits] = useState<Record<string, StudioDepartureKitStatusV1 | null>>(
    {},
  );
  const [projectBusinessNames, setProjectBusinessNames] = useState<Record<string, string | null>>({});
  const [projectLifecycleSummaries, setProjectLifecycleSummaries] = useState<
    Record<string, StudioProjectLifecycleSummaryV1 | null>
  >({});

  const patchProjectEnrichment = useCallback(
    (
      projectId: string,
      patch: {
        readonly review?: ClientReviewStatusV1 | null;
        readonly departureKit?: StudioDepartureKitStatusV1 | null;
        readonly businessName?: string | null;
        readonly lifecycleSummary?: StudioProjectLifecycleSummaryV1 | null;
      },
    ) => {
      if (patch.review !== undefined) {
        setProjectReviews((current) => ({ ...current, [projectId]: patch.review ?? null }));
      }
      if (patch.departureKit !== undefined) {
        setProjectDepartureKits((current) => ({ ...current, [projectId]: patch.departureKit ?? null }));
      }
      if (patch.businessName !== undefined) {
        setProjectBusinessNames((current) => ({ ...current, [projectId]: patch.businessName ?? null }));
      }
      if (patch.lifecycleSummary !== undefined) {
        setProjectLifecycleSummaries((current) => ({
          ...current,
          [projectId]: patch.lifecycleSummary ?? null,
        }));
      }
    },
    [],
  );

  useEffect(() => {
    const allProjects = projectsState.data ?? [];
    if (allProjects.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        const lifecycleSummaries = await api.listProjectLifecycleSummaries();
        if (cancelled) return;
        setProjectLifecycleSummaries(
          Object.fromEntries(lifecycleSummaries.map((row) => [row.projectId, row])),
        );
        setProjectReviews(
          Object.fromEntries(lifecycleSummaries.map((row) => [row.projectId, reviewFromLifecycleSummary(row)])),
        );
        setProjectDepartureKits(
          Object.fromEntries(
            lifecycleSummaries.map((row) => [row.projectId, departureKitFromLifecycleSummary(row)]),
          ),
        );
        setProjectBusinessNames(
          Object.fromEntries(
            lifecycleSummaries.map((row) => [
              row.projectId,
              row.businessName ?? readProjectDisplayName(row.projectId) ?? null,
            ]),
          ),
        );
      } catch {
        if (cancelled) return;
        const legacyRows = await Promise.all(
          allProjects.map(async (project) => {
            try {
              const review = await api.getClientReviewStatus(project.projectId);
              return {
                projectId: project.projectId,
                review,
                businessName:
                  review.clientLink?.clientDisplayName ?? readProjectDisplayName(project.projectId),
              };
            } catch {
              return {
                projectId: project.projectId,
                review: null,
                businessName: readProjectDisplayName(project.projectId),
              };
            }
          }),
        );
        if (cancelled) return;
        setProjectLifecycleSummaries({});
        setProjectReviews(Object.fromEntries(legacyRows.map((row) => [row.projectId, row.review])));
        setProjectDepartureKits({});
        setProjectBusinessNames(
          Object.fromEntries(legacyRows.map((row) => [row.projectId, row.businessName ?? null])),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, projectsState.data]);

  return {
    projectReviews,
    projectDepartureKits,
    projectBusinessNames,
    projectLifecycleSummaries,
    patchProjectEnrichment,
  };
}
